import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * MCX fallback
 * Certaines apps de personnalisation peuvent ajouter un produit au panier
 * sans déclencher le CartAddEvent natif du thème.
 *
 * On détecte donc aussi les ajouts Shopify réussis via /cart/add
 * et on émet un événement de secours.
 */
const MCX_CART_ADD_EVENT = 'mcx:cart-added';

if (!window.__mcxCartAddFallbackInstalled) {
  window.__mcxCartAddFallbackInstalled = true;

  /**
   * Fallback FETCH
   */
  const originalFetch = window.fetch;

  window.fetch = async (...args) => {
    const response = await originalFetch(...args);

    try {
      const request = args[0];
      const url =
        typeof request === 'string'
          ? request
          : request?.url || '';

      if (
        response.ok &&
        (
          url.includes('/cart/add') ||
          url.includes('/cart/add.js')
        )
      ) {
        window.dispatchEvent(new CustomEvent(MCX_CART_ADD_EVENT));
      }
    } catch (error) {
      // On ne bloque jamais l'ajout au panier
      console.warn('[MCX] Cart fallback fetch:', error);
    }

    return response;
  };

  /**
   * Fallback XMLHttpRequest
   * Au cas où une app utilise encore XHR plutôt que fetch.
   */
  const originalOpen = XMLHttpRequest.prototype.open;
  const originalSend = XMLHttpRequest.prototype.send;

  XMLHttpRequest.prototype.open = function (method, url, ...rest) {
    this.__mcxCartUrl = url;
    return originalOpen.call(this, method, url, ...rest);
  };

  XMLHttpRequest.prototype.send = function (...args) {
    this.addEventListener(
      'load',
      function () {
        try {
          const url = String(this.__mcxCartUrl || '');

          if (
            this.status >= 200 &&
            this.status < 300 &&
            (
              url.includes('/cart/add') ||
              url.includes('/cart/add.js')
            )
          ) {
            window.dispatchEvent(new CustomEvent(MCX_CART_ADD_EVENT));
          }
        } catch (error) {
          console.warn('[MCX] Cart fallback XHR:', error);
        }
      },
      { once: true }
    );

    return originalSend.apply(this, args);
  };
}

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  #lastOpenTime = 0;

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(
      CartAddEvent.eventName,
      this.#handleCartAdd
    );

    window.addEventListener(
      MCX_CART_ADD_EVENT,
      this.#handleCartAddFallback
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(
      CartAddEvent.eventName,
      this.#handleCartAdd
    );

    window.removeEventListener(
      MCX_CART_ADD_EVENT,
      this.#handleCartAddFallback
    );
  }

  /**
   * Ouverture sécurisée du drawer.
   * Le petit délai anti-doublon évite que le drawer soit ouvert
   * deux fois si Tinker ET le fallback détectent le même ajout.
   */
  #safeOpen = () => {
    if (!this.hasAttribute('auto-open')) return;

    const now = Date.now();

    if (now - this.#lastOpenTime < 500) return;

    this.#lastOpenTime = now;

    requestAnimationFrame(() => {
      this.showDialog();
    });
  };

  #handleCartAdd = () => {
    this.#safeOpen();
  };

  #handleCartAddFallback = () => {
    this.#safeOpen();
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked
     * to avoid overlapping dialogs.
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent =
        document.querySelector('shopify-payment-terms')?.shadowRoot;

      const cta =
        installmentsContent?.querySelector('#shopify-installments-cta');

      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define(
    'cart-drawer-component',
    CartDrawerComponent
  );
}